import { ApolloClient, InMemoryCache, createHttpLink, from, ApolloLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import Utils from '../utility';
import { API_GRAPH_BASE } from '../config/env';

const EMPTY_ORG_ID = 'Empty';
const LOCAL_STORAGE_ORG_KEY = 'current_organization';

const getCurrentOrganizationId = (): string =>
  localStorage.getItem(LOCAL_STORAGE_ORG_KEY) || EMPTY_ORG_ID;

// Define your GraphQL API endpoint
const httpLink = createHttpLink({
  uri: `${API_GRAPH_BASE}/graphql`,
});

// Session‐expiry handler
const handleSessionExpiry = () => {
  // 1) clear any stored tokens
  Utils.clearTokenCookies();

  // 2) queue the toast for the login page
  localStorage.setItem('LogoutToast', 'Your session has expired. Please sign in again.');

  // 3) redirect immediately
  const slug = localStorage.getItem('tenant-slug') || '';
  window.location.href = slug ? `/${slug}/login` : '/login';
};

// Error handling link
type GraphQLErrorExtensions = {
  code?: string;
  [key: string]: unknown;
};

type NetworkErrorWithStatus = Error & { statusCode?: number; status?: number };

const errorLink = onError(({ graphQLErrors, networkError }) => {
  // catch token‐expiry GraphQL errors
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      // if your backend tags unauthenticated with code 'UNAUTHENTICATED'
      const extensions = err.extensions as GraphQLErrorExtensions | undefined;
      if (extensions?.code === 'UNAUTHENTICATED') {
        return handleSessionExpiry();
      }
    }
  }

  // catch HTTP 401/403 at network layer
  if (networkError) {
    const netErr = networkError as NetworkErrorWithStatus;
    const status = netErr.statusCode ?? netErr.status;
    if (status === 401 || status === 403) {
      return handleSessionExpiry();
    }
  }
});

// Authentication link to add headers like authorization tokens
const authLink = new ApolloLink((operation, forward) => {
  // Get the authentication token from local storage if it exists
  const token = Utils.getCookie('auth-token');

  const context = operation.getContext();
  const skipOrgId = context.skipOrgId === true;

  // Add the authorization to the headers
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      ...(skipOrgId ? {} : { 'x-organization-id': getCurrentOrganizationId() }),
    },
  }));

  return forward(operation);
});

// Initialize Apollo Client
const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
});

export default client;
